import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getProfileDisplayName } from '../utils/profile';
import { DaypopBrand } from './WaypointLogo';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Lightbulb,
  Target,
  Sparkles,
  GitBranch,
  Folder,
  Shield,
  Settings,
  HelpCircle
} from 'lucide-react';

const Sidebar = ({ user, dbUser }) => {
  const sidebarName = getProfileDisplayName(user, dbUser, 'Account');
  const location = useLocation();
  const isAdmin = dbUser?.role === 'admin' || dbUser?.role === 'master_admin';

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: BookOpen, label: 'Log', path: '/log' },
    { icon: Users, label: 'Orbit', path: '/orbit' },
    { icon: Lightbulb, label: 'Lessons', path: '/lessons' },
    { icon: Target, label: 'Goals', path: '/goals' },
    { icon: Sparkles, label: 'Ideas', path: '/ideas' },
    { icon: GitBranch, label: 'Decisions', path: '/decisions' },
    { icon: Folder, label: 'Journey', path: '/journey' },
  ];

  if (isAdmin) {
    navItems.push({ icon: Shield, label: 'Admin', path: '/admin' });
  }

  return (
    <div className="w-64 bg-bg-surface/80 backdrop-blur-xl border-r border-border h-screen fixed left-0 top-0 flex flex-col custom-scrollbar">
      {/* Logo */}
      <div className="p-6 border-b border-border/50">
        <DaypopBrand logoSize={40} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto custom-scrollbar">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`group flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 relative ${
                isActive
                  ? 'bg-gold text-white font-semibold shadow-sm'
                  : 'text-text-secondary hover:bg-bg-surface-alt/80 hover:text-text-primary'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon size={18} strokeWidth={isActive ? 2.5 : 2} className="shrink-0" />
              <span className="text-sm">{item.label}</span>
              {isActive && (
                <div className="absolute right-3 w-1.5 h-1.5 bg-white/60 rounded-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-3 border-t border-border/50 space-y-0.5">
        <Link
          to="/help"
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
            location.pathname === '/help'
              ? 'bg-gold text-white font-semibold shadow-sm'
              : 'text-text-secondary hover:bg-bg-surface-alt/80 hover:text-text-primary'
          }`}
        >
          <HelpCircle size={18} strokeWidth={2} className="shrink-0" />
          <span className="text-sm">Help</span>
        </Link>
        <Link
          to="/settings"
          className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-bg-surface-alt/80 transition-all duration-200 group"
        >
          <div className="w-8 h-8 bg-gold-light rounded-lg flex items-center justify-center">
            <Settings size={16} className="text-gold group-hover:rotate-45 transition-transform duration-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-text-primary truncate">{sidebarName}</div>
            <div className="text-xs text-text-tertiary">{dbUser?.plan === 'pro' ? 'Pro' : 'Free plan'}</div>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Sidebar;
